import {
  type Collection,
  List as ImList,
  Map as ImMap,
  Record as ImRecord,
  Set as ImSet,
  type RecordOf,
} from 'immutable';
import isEqual from 'lodash/isEqual';
import isEqualWith from 'lodash/isEqualWith';
import { DateTime, type Interval } from 'luxon';
import { FlatMapCache, LruMapCache, LruObjectCache, createCachedSelector } from 're-reselect';
import { createSelector, lruMemoize, weakMapMemoize } from 'reselect';

import {
  type ACCOUNTS_REDUCER_KEY,
  accountsSelectors,
  accountsTypes,
  accountsUtils,
} from 'app-components/flux/accounts';
import { type CATEGORIES_REDUCER_KEY, categoriesSelectors } from 'app-components/flux/categories';
import { chartOfAccountsSelectors, chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import type { ChartOfAccount, ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { selectorsHelpers } from 'app-components/flux/core';
import { getId } from 'app-components/flux/core/resourceStoreTypes';
import { dateSelectors } from 'app-components/flux/date';
import { type FEATURE_FLAGS_REDUCER_KEY, featureFlagsSelectors } from 'app-components/flux/feature-flags';
import type * as filtersTypes from 'app-components/flux/filters/filtersTypes';
import { investmentHoldingsV2Selectors } from 'app-components/flux/investment-holdings-v2';
import type { ScheduledTransactionType } from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { SECURITIES_REDUCER_KEY } from 'app-components/flux/securities/securitiesTypes';
import { tagsSelectors } from 'app-components/flux/tags';
import { getLogger } from 'app-components/utils/core';
import { createAppSelector, createWeakMapSelector } from 'app-components/utils/core/reselect';
import { intervalContainsInclusive } from 'app-components/utils/date';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { shallowEqual } from 'react-redux';
import { getAccountsById } from '../accounts/accountsSelectors';
import { isQBPAccount } from '../accounts/accountsUtils';
import { Category } from '../categories/categoriesTypes';
import { chartOfAccountsUtils } from '../chart-of-accounts';
import { getCurrentDateString } from '../date/dateSelectors';
import { DATE_REDUCER_KEY } from '../date/dateTypes';
import {
  type Allocation,
  type AllocationProps,
  type QcsColor,
  type SplitItemProps,
  StatusChipTypes,
  TRANSACTIONS_REDUCER_KEY,
  type TransactionItem,
  type TransactionItemCombinedProps,
  type TransactionProps,
  type TransactionRecord,
  mkTransactionItem,
} from './transactionsTypes';
import * as transactionsUtils from './transactionsUtils';

const log = getLogger('flux/transactions/transactionsSelectors.js');

type State = Pick<
  RootState,
  | typeof TRANSACTIONS_REDUCER_KEY
  | typeof ACCOUNTS_REDUCER_KEY
  | typeof SECURITIES_REDUCER_KEY
  | typeof CATEGORIES_REDUCER_KEY
  | typeof FEATURE_FLAGS_REDUCER_KEY
  | typeof DATE_REDUCER_KEY
>;

export const getTransactionsStore = (state: State) => state[TRANSACTIONS_REDUCER_KEY];
export const getLoadPending = (state: State) => getTransactionsStore(state).loadPending;
export const getError = (state: State) => getTransactionsStore(state).error;
export const getIsLoading = (state: State) => getTransactionsStore(state).isLoading;
export const getLastSyncDate = (state: State) => getTransactionsStore(state).lastSyncDate;
export const getResourcesById = (state: State) => getTransactionsStore(state).resourcesById;
export const getDateOnAfter = (state: State) => getTransactionsStore(state).dateOnAfter;
export const getDateOnBefore = (state: State) => getTransactionsStore(state).dateOnBefore;
export const getEarliestDateOn = (state: State) => getTransactionsStore(state).earliestDateOn;
export const getLastReductions = (state: State) => getTransactionsStore(state).lastReductions;

// NOTE: this is to break circular dependencies
const getSecuritiesResourcesById = (state: State) => state[SECURITIES_REDUCER_KEY].resourcesById;

export const isAllTransactionsAvailable = createSelector(
  getDateOnAfter,
  getEarliestDateOn,
  (dateOnAfter, earliestDateOn) => (dateOnAfter || '') < (earliestDateOn || ''),
);

export const existsTransactionWithTag = createSelector(
  getResourcesById,
  (_: unknown, { id }: { id: string }) => id,
  (state, tagId) => Boolean(state.find((txn) => Boolean(transactionsUtils.txnHasTag(txn, tagId)))),
);

export const getTransactionsByAccountIdRaw = createSelector(
  getResourcesById,
  // the typedefs for groupBy return a general keyed sequence, but we need an immutable map that has `withMutations`, and we know it does have this method since this selector works in production
  (transactions) =>
    transactions.groupBy((transaction) => transaction.accountId) as unknown as ImMap<
      string,
      ImMap<string, TransactionRecord>
    >,
);

export const TxnTypes = Object.freeze({
  GOALS: 1,
  REGULAR_TXNS: 2,
  UNACCEPTED_SCHEDULED_TXNS: 4,
  REFUND_VIRTUAL_TXN: 8,
  INVESTMENT_TXN: 16,
  A2A_TRANSFER: 32,
  A2A_SHADOW: 64,
  ESTIMATES: 128,

  ALL: 0xffff,
});
export const txnTypesCash = TxnTypes.REGULAR_TXNS | TxnTypes.UNACCEPTED_SCHEDULED_TXNS;
export const txnTypesDefault = txnTypesCash | TxnTypes.INVESTMENT_TXN;
export const txnTypesCashAndRefund = txnTypesCash | TxnTypes.REFUND_VIRTUAL_TXN;
export const txnTypesCashAndPlanned = txnTypesCashAndRefund | TxnTypes.A2A_TRANSFER;
export const txnTypesEstimates = txnTypesCashAndRefund | TxnTypes.ESTIMATES;

export const transactionOfTypes = (
  txn: TransactionProps,
  goalAccountIds: ImSet<string>,
  txnTypes = txnTypesDefault,
  todayISO?: string,
) => {
  if (transactionsUtils.isRefundIncompleteTxn(txn, todayISO)) {
    return txnTypes & TxnTypes.REFUND_VIRTUAL_TXN;
  }
  if (transactionsUtils.isGoalTxnInNonGoalAccount(txn, goalAccountIds) || txn.subtype === 'GOAL_STARTING_AMOUNT') {
    return txnTypes & TxnTypes.GOALS;
  }
  if (transactionsUtils.isUnacceptedScheduledTxn(txn)) {
    return txnTypes & TxnTypes.UNACCEPTED_SCHEDULED_TXNS;
  }
  if (txn.type === 'INVESTMENT') {
    return txnTypes & TxnTypes.INVESTMENT_TXN;
  }
  if (transactionsUtils.isPlannedA2ATransfer(txn)) {
    return txnTypes & TxnTypes.A2A_TRANSFER;
  }
  if (transactionsUtils.isCancelledA2aTransfer(txn)) {
    return txnTypes & TxnTypes.A2A_SHADOW;
  }
  if (transactionsUtils.isEstimateTransaction(txn)) {
    return txnTypes & TxnTypes.ESTIMATES;
  }
  return txnTypes & TxnTypes.REGULAR_TXNS;
};

export const getTransactionsOfTypes = createCachedSelector(
  getResourcesById,
  accountsSelectors.getGoalAccountIds,
  (_state: unknown, txnTypes: number | undefined) => txnTypes || txnTypesDefault,
  getCurrentDateString,
  (transactionsById, goalAccountIds, txnTypes, todayISO) =>
    transactionsById.filter((transaction) => transactionOfTypes(transaction, goalAccountIds, txnTypes, todayISO)),
)({
  keySelector: (_state, txnTypes) => txnTypes || 'default',
});

export const getTransactionsById = (state: State) => getTransactionsOfTypes(state, txnTypesDefault);

// getTransactionsForAccountIdSelectors
//
// This selector returns a map of selectors that can then be used to get transactions
// filtered by txn-types for a specific account.
//
// We are using this level of indirection so that the returned transactions for each
// account will not be invalidated by the adding/removing of accounts or by changing
// transactional data in other accounts.
//
// The selectors returned by this method are not typical selectors. Instead of
// a state and props, the expected arguments are a map of transactions by
// account ID.
//
export const getTransactionsForAccountIdSelectors = createCachedSelector(
  accountsSelectors.getAllAccountIds,
  accountsSelectors.getGoalAccountIds,
  (_state: unknown, props: { txnTypes?: number } | null | undefined) => (props && props.txnTypes) || txnTypesDefault,
  getCurrentDateString,
  (allAccountIds, goalAccountIds, txnTypes, todayISO) =>
    ImMap<
      string,
      (accountsById: ImMap<string, ImMap<string, TransactionRecord>>) => ImMap<string, TransactionRecord>
    >().withMutations((map) => {
      allAccountIds.forEach((accountId) => {
        const selector = createSelector(
          (transactionsByAccountId: ImMap<string, ImMap<string, TransactionRecord>>) =>
            transactionsByAccountId.get(accountId),
          (txnsById) => {
            // handle special case of no transactions in account
            if (txnsById === undefined || txnsById.size === 0) {
              return ImMap<string, TransactionRecord>();
            }
            const filteredTxnsById = txnsById.filter((txn) =>
              transactionOfTypes(txn, goalAccountIds, txnTypes, todayISO),
            );
            return filteredTxnsById;
          },
        );
        map.set(accountId, selector);
      });
    }),
)({
  keySelector: (_state: unknown, props: { txnTypes?: number } | null | undefined) =>
    (props && props.txnTypes) || txnTypesDefault,
  cacheObject: new FlatMapCache(),
});

export const getTransactionsByAccountId = createSelector(
  [
    accountsSelectors.getAllAccountIds,
    getTransactionsByAccountIdRaw,
    (state: State, props?: { txnTypes?: number }) => getTransactionsForAccountIdSelectors(state, props),
  ],
  (allAccountIds, txnsByAccountId, txnsForAccountIdSelectors) =>
    ImMap<string, ImMap<string, TransactionRecord>>().withMutations((map) => {
      allAccountIds.forEach((accountId) => {
        const txnsForAccountIdSelector = txnsForAccountIdSelectors.get(accountId);
        if (txnsForAccountIdSelector) {
          map.set(accountId, txnsForAccountIdSelector(txnsByAccountId));
        }
      });
    }),
  {
    memoize: lruMemoize,
    memoizeOptions: {
      maxSize: 10,
      resultEqualityCheck: shallowEqual,
      equalityCheck: shallowEqual,
    },
  },
);

export const getFlaggedTransactions = createSelector(getResourcesById, (allTransactions) =>
  allTransactions
    .filter(({ userFlag }) => userFlag)
    .sortBy(({ modifiedAt }) => modifiedAt)
    .reverse()
    .toList(),
);

export const getUsedFlags = createSelector(
  (state: State) => getFlaggedTransactions(state),
  (allFlaggedTransactions) => ImSet(allFlaggedTransactions.map(({ userFlag }) => userFlag as QcsColor)), // getFlaggedTransactions has already filtered out any txn without a flag field
);

const sortStDueOn = (a: TransactionProps, b: TransactionProps) =>
  (a.stDueOn || '') < (b.stDueOn || '') ? -1 : (a.stDueOn || '') > (b.stDueOn || '') ? 1 : 0;

export const pastTransactions = createSelector(
  getTransactionsByAccountId,
  (_state: unknown, props: { id: string }) => props.id,
  (_state: unknown, props: { transaction?: TransactionProps }) => props.transaction?.accountId,
  (transactionsByAccountId, stModelId, accountId) => {
    const allTxn = transactionsByAccountId.get(accountId || '');
    return (
      allTxn &&
      allTxn.filter((d) => transactionsUtils.isAcceptedScheduledTxn(d) && d.stModelId === stModelId).sort(sortStDueOn)
    );
  },
);

export const getAllUpcomingTransactions = createSelector(
  getTransactionsByAccountId,
  (_state: unknown, schTxns: ImMap<string, RecordOf<ScheduledTransactionType>>) => schTxns,
  (transactionsByAccountId, schTxns) => {
    if (!transactionsByAccountId || transactionsByAccountId.isEmpty()) {
      return ImMap<string, ImMap<string, TransactionRecord>>();
    }

    return ImMap<string, ImMap<string, TransactionRecord>>().withMutations((mutableMap) => {
      schTxns.forEach(({ id, transaction }) => {
        if (transaction?.accountId) {
          const allTxn = transactionsByAccountId.get(transaction.accountId);
          const allUpcomingTxn =
            allTxn &&
            allTxn
              .filter((txn) => transactionsUtils.isUnacceptedScheduledTxn(txn) && txn.stModelId === id)
              .sort(sortStDueOn);
          if (id && allUpcomingTxn && !allUpcomingTxn.isEmpty()) {
            mutableMap.set(id, allUpcomingTxn);
          }
        }
      });
    });
  },
);

export const getRefundActiveTransactions = createSelector(
  (state: State) => getTransactionsOfTypes(state, txnTypesDefault | TxnTypes.REFUND_VIRTUAL_TXN),
  getCurrentDateString,
  (transactionsById: ImMap<string, TransactionRecord>, todayISO) =>
    transactionsById
      .filter((transaction) => {
        const refundState = transactionsUtils.getRefundState(transaction, todayISO);
        return (
          refundState === transactionsUtils.RefundState.EXPECTED ||
          refundState === transactionsUtils.RefundState.OVERDUE
        );
      })
      .sortBy((transaction) => transaction.postedOn),
);

export const getRefundActiveTransactionsByAccountId = createCachedSelector(
  getRefundActiveTransactions,
  (_state: unknown, accountId: string) => accountId,
  (refundActiveTransactions, accountId) =>
    refundActiveTransactions.filter((transaction) => transaction.accountId === accountId),
)({
  keySelector: (_state: unknown, accountId) => accountId || 'none',
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

export const getRefundActiveTransactionsTotal = createSelector(getRefundActiveTransactions, (transactionsById) =>
  transactionsById.reduce((total, transaction) => total + Number(transaction.amount ?? 0), 0),
);

export const getRefundActiveRelatedTxns = createCachedSelector(
  getRefundActiveTransactions,
  (_state: unknown, relatedTnxId: string) => relatedTnxId,
  (refundActiveTransactions, relatedTnxId) =>
    refundActiveTransactions.filter((transaction) => transaction.subtypeRelatedTxn?.id === relatedTnxId),
)({
  keySelector: (_state: unknown, relatedTnxId?: string) => relatedTnxId ?? '',
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

export const getRefundCompleteTransactions = createSelector(
  (state: State) => getTransactionsOfTypes(state, txnTypesDefault | TxnTypes.REFUND_VIRTUAL_TXN),
  getCurrentDateString,
  (transactionsById, todayISO) =>
    transactionsById
      .filter((transaction) => {
        const refundState = transactionsUtils.getRefundState(transaction, todayISO);
        return (
          refundState === transactionsUtils.RefundState.COMPLETE || refundState === transactionsUtils.RefundState.SHADOW
        );
      })
      .sort((transaction1, transaction2) => transaction2.postedOn?.localeCompare(transaction1.postedOn) ?? 0),
);

export const getRefundCompleteTransactionsTotal = createSelector(getRefundCompleteTransactions, (transactionsById) =>
  transactionsById.reduce((total, transaction) => total + Number(transaction.amount ?? 0), 0),
);

export const getRefundCompleteRelatedTxns = createCachedSelector(
  getRefundCompleteTransactions,
  (_state: unknown, relatedTnxId: string) => relatedTnxId,
  (refundCompleteTransactions, relatedTnxId) =>
    refundCompleteTransactions.filter((transaction) => transaction.subtypeRelatedTxn?.id === relatedTnxId),
)({
  keySelector: (_state: unknown, relatedTnxId?: string) => relatedTnxId ?? '',
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

export const getRefundLinkEligibleTransactions = createCachedSelector(
  (state: State, transaction: TransactionProps) =>
    getTransactionsByAccountId(state || store?.getState()).get(transaction.accountId || ''),
  (transactions) =>
    transactions
      ?.filter((transaction) => (transaction.amount || 0) > 0)
      .filter((transaction) => transaction.matchState === 'NOT_MATCHED')
      .filter(transactionsUtils.isBankDownloadedTxn)
      .sortBy((transaction) => transaction.postedOn)
      .reverse(),
)({
  keySelector: (_state: unknown, transaction?: TransactionProps) => transaction?.accountId ?? '',
  cacheObject: new LruObjectCache({ cacheSize: 12 }),
});

export const getTransactionsForAccountIds = createCachedSelector(
  (_state: unknown, props: { accountIds?: string[] | ImList<string> | null }) => props.accountIds,
  getTransactionsByAccountId,
  accountsSelectors.getAccountsById,
  (accountIds, transactionsByAccountId, accountsById) => {
    let accountIdsList = ImList(accountIds || []);
    if (!accountIds || accountIdsList.size === 0) {
      accountIdsList = accountsById.map(getId).toList();
    }

    let returnTransactions = ImMap<string, TransactionRecord>();
    accountIdsList.forEach((id) => {
      const txns = transactionsByAccountId.get(id);
      if (txns) {
        returnTransactions = returnTransactions.merge(txns);
      }
    });

    return returnTransactions;
  },
)({
  keySelector: (_state: unknown, props: { accountIds?: string[] | ImList<string> | null }) =>
    props.accountIds ? ImList(props.accountIds).hashCode() : 'all',
  cacheObject: new FlatMapCache(),
});

// used for excluding upcoming transactions + investment transactions + connected loan txns for txnPage on mobile
export const getTransactionsPageTransactions = createSelector(
  getTransactionsForAccountIds,
  dateSelectors.getCurrentDateString,
  accountsSelectors.getAccountsById,
  (state: State): string => featureFlagsSelectors.getFeatureFlag(state, 'exposeInvestmentTransactions') || 'HIDE',
  (state: State): boolean => featureFlagsSelectors.getFeatureFlags(state).get('hideConnectedLoanTransactions'),
  (transactions, today, accounts, exposeInvestmentTransactions, hideConnectedLoanTransactions) =>
    transactions.filter(
      (txn) =>
        transactionsUtils.isExposedTransaction(txn, accounts, exposeInvestmentTransactions) &&
        !(hideConnectedLoanTransactions && transactionsUtils.isConnectedLoanTransaction(txn, accounts)) &&
        !transactionsUtils.isUnacceptedScheduledTxn(txn) &&
        (txn.postedOn ?? 0) <= today,
    ),
);

export const getTransactionById = (state: State, id: string) => getResourcesById(state).get(id);

export type CategorySummaryProps = {
  count: number;
  amount: number;
  amountByMonth: ImMap<number, number> | null;
  coa: ChartOfAccountProps | null;
  acctId: string | null;
  monthlyAverage: string | null;
};
export const CategorySummary = ImRecord<CategorySummaryProps>({
  count: 0,
  amount: 0,
  amountByMonth: null,
  coa: null,
  acctId: null,
  monthlyAverage: null,
});

export const getMostPopularCatsByAccountId = createCachedSelector(
  getTransactionsByAccountId,
  (_state: unknown, props: { dateRange?: Interval } | undefined) => props && props.dateRange,
  (_state: unknown, props: { sortByAmount?: boolean } | undefined) => props && props.sortByAmount,
  (txns, dateRange, sortByAmount) => {
    let catCount = ImMap<string, RecordOf<CategorySummaryProps>>();

    txns.forEach((acctTxns, acctId) => {
      acctTxns.forEach((txn) => {
        if (txn.coa && txn.coa.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY) {
          // ignore splits, transfers, and uncategorized

          // check date if it is supplied
          if (!dateRange || intervalContainsInclusive(dateRange, DateTime.fromISO(txn.postedOn))) {
            const catRecord = catCount.get(txn.coa.id);
            let amount = Number(txn.amount);
            let count = 1;
            const { month } = DateTime.fromISO(txn.postedOn);
            let amountByMonth = ImMap<number, number>().set(month, amount);

            if (catRecord && catRecord.amountByMonth) {
              amount = catRecord.amount + Number(txn.amount ?? 0);
              count = catRecord.count + 1;
              amountByMonth = catRecord.amountByMonth.set(month, (catRecord.amountByMonth.get(month) || 0) + amount);
            }
            catCount = catCount.set(
              txn.coa.id,
              new CategorySummary({
                count,
                amount,
                amountByMonth,
                coa: txn.coa,
                acctId,
              }),
            );
          }
        }
      });
    });
    if (sortByAmount) {
      return catCount.toList().sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    }
    return catCount.toList().sort((a, b) => b.count - a.count);
  },
)({
  keySelector: (
    _state: unknown,
    props: { dateRange?: Interval; sortByAmount?: boolean } | undefined,
    selectorKey?: string,
  ) =>
    ((props && props.dateRange && props.dateRange.toISO()) || 'nodate') + (props && props.sortByAmount) + selectorKey ||
    '',
  cacheObject: new LruObjectCache({ cacheSize: 5 }),
});

export const getPopularCatsMonthlyAverage = createCachedSelector(
  (state: State, props: { dateRange?: Interval; sortByAmount?: boolean } | undefined) =>
    getMostPopularCatsByAccountId(state, props),
  (popularCats) => {
    // must occur more than once
    let filteredCats = popularCats.filter((x) => (x.amountByMonth?.size || 0) > 1);

    // add in monthly average
    filteredCats = filteredCats.map((x) =>
      x.set('monthlyAverage', (x.amount / (x.amountByMonth?.size || 1)).toFixed(0)),
    );

    // sort by monthly average
    filteredCats = filteredCats.sort((a, b) => {
      if (a.amountByMonth?.size === b.amountByMonth?.size) {
        return Number(b.monthlyAverage || 0) - Number(a.monthlyAverage || 0);
      }
      return (
        (b.amountByMonth?.size || 1) * 5000 +
        Math.abs(b.amount) -
        ((a.amountByMonth?.size || 1) * 5000 + Math.abs(a.amount))
      );
    });

    return filteredCats;
  },
)({
  keySelector: (
    _state: unknown,
    props: { dateRange?: Interval; sortByAmount?: boolean } | undefined,
    selectorKey?: string,
  ) => (props?.dateRange?.toISO() || 'nodate') + props?.sortByAmount + (selectorKey ?? ''),
  cacheObject: new LruObjectCache({ cacheSize: 5 }),
});

export const getTransactionsByIds = createCachedSelector(
  getResourcesById,
  (_state: unknown, ids: ImList<string> | null | undefined) => ids,
  (transactionsById, ids) => {
    if (!ids || ids.size === 0 || !transactionsById?.size) {
      return ImList<TransactionRecord>();
    }
    const retArr = ids
      .map((id) => {
        if (!transactionsById.has(id)) {
          log.warn(`Transaction with id ${id} not found`);
          return undefined;
        }
        return transactionsById.get(id);
      })
      ?.filter(Boolean) as ImList<RecordOf<TransactionProps>>;

    return retArr.toList();
  },
)({
  keySelector: (_state: unknown, ids: ImList<string> | null | undefined, selectorKey?: string) =>
    (ids?.hashCode() ?? '') + (selectorKey ?? ''),
  cacheObject: new LruObjectCache({ cacheSize: 30 }),
});

export const getTransactionsByFilterFn = createCachedSelector(
  (state: State, props: { transactions?: ImMap<string, TransactionRecord> } | undefined | null) =>
    props?.transactions || getTransactionsById(state),
  (_state: unknown, props: { filterFn?: (txn: TransactionRecord, props: any) => boolean } & Record<string, any>) =>
    props,
  (transactionsById, props) =>
    transactionsById.filter((transaction) => !props || !props.filterFn || props.filterFn(transaction, props)),
)({
  keySelector: (
    _state: unknown,
    props: { filterFnGUID?: string; transactions?: ImMap<string, TransactionRecord> } | undefined | null,
  ) => (props && props.filterFnGUID) || '',
  cacheObject: new LruObjectCache({ cacheSize: 13 }),
});

type Filter =
  | {
      accountIds?: ImList<string>;
      accountIdsArray?: string[];
      startDate?: DateTime;
      endDate?: DateTime;
      payee?: string;
      amountSign?: number;
      rootCOAs?: Collection<string, ChartOfAccount> & { size: number };
      coas?: Collection<string, ChartOfAccount> & { size: number };
      isExcludedFromF2S?: boolean;
      isScheduled?: boolean;
      isBill?: boolean;
      isSubscription?: boolean;
      isIncome?: boolean;
      transactions?: ImMap<string, TransactionRecord>;
    }
  | undefined
  | null;

export const getTransactionsByFilter = createCachedSelector(
  (state: State, filter: Filter) => filter?.transactions || getTransactionsById(state),
  categoriesSelectors.getIncomeCOAIds,
  accountsSelectors.getGoalAccountIds,
  (_state: unknown, filter: Filter) => filter,
  (transactionsById, incomeCoas, goalAccountIds, filter) => {
    const {
      accountIds,
      accountIdsArray,
      startDate: startLocalDate,
      endDate: endLocalDate,
      payee,
      amountSign,
      rootCOAs,
      coas,
      isExcludedFromF2S,
      isScheduled,
      isBill,
      isSubscription,
      isIncome,
    } = filter || {};
    return (
      transactionsById &&
      transactionsById.filter((transaction) => {
        let keepTransaction = !goalAccountIds.contains(transaction.accountId || '');

        keepTransaction = keepTransaction && payee !== undefined ? transaction.payee === payee : keepTransaction;
        keepTransaction =
          keepTransaction && amountSign !== undefined
            ? Math.sign(Number(transaction.amount ?? 0)) === Math.sign(amountSign)
            : keepTransaction;
        keepTransaction =
          keepTransaction && isScheduled !== undefined
            ? Boolean(transaction.stModelId) === Boolean(isScheduled)
            : keepTransaction;
        keepTransaction =
          keepTransaction && isExcludedFromF2S !== undefined
            ? Boolean(transaction.isExcludedFromF2S) === Boolean(isExcludedFromF2S)
            : keepTransaction;
        keepTransaction =
          keepTransaction && accountIds && accountIds.size > 0
            ? accountIds.contains(transaction.accountId || '')
            : keepTransaction;
        keepTransaction =
          keepTransaction && accountIdsArray && accountIdsArray.length > 0
            ? accountIdsArray.includes(transaction.accountId || '')
            : keepTransaction;
        const postedOnLocalDate = keepTransaction && DateTime.fromISO(transaction.postedOn);
        keepTransaction = keepTransaction && startLocalDate ? postedOnLocalDate >= startLocalDate : keepTransaction;
        keepTransaction = keepTransaction && endLocalDate ? postedOnLocalDate <= endLocalDate : keepTransaction;
        keepTransaction =
          keepTransaction && rootCOAs && rootCOAs.size > 0
            ? Boolean(
                transaction.coa &&
                  rootCOAs.find((coa) => coa.type === transaction.coa?.type && coa.id === transaction.coa?.id),
              )
            : keepTransaction;
        keepTransaction = coas && !coas.size ? false : keepTransaction;
        keepTransaction =
          keepTransaction && coas && coas.size > 0
            ? Boolean(
                (transaction.coa &&
                  coas.find((coa) => coa.type === transaction.coa?.type && coa.id === transaction.coa?.id)) ||
                  (transaction.split &&
                    transaction.split.items &&
                    transaction.split.items.find((splitItem) =>
                      Boolean(coas.find((coa) => coa.type === splitItem.coa?.type && coa.id === splitItem.coa.id)),
                    )),
              )
            : keepTransaction;
        keepTransaction =
          keepTransaction && isBill !== undefined ? Boolean(transaction.isBill) === Boolean(isBill) : keepTransaction;
        keepTransaction =
          keepTransaction && isSubscription !== undefined
            ? Boolean(transaction.isSubscription) === Boolean(isSubscription)
            : keepTransaction;
        keepTransaction =
          keepTransaction && isIncome !== undefined
            ? transactionsUtils.isIncomeFromTxn(transaction, incomeCoas) === Boolean(isIncome)
            : keepTransaction;

        return keepTransaction;
      })
    );
  },
)({
  keySelector: (_state: unknown, filter: Filter) =>
    filter
      ? `${filter.isExcludedFromF2S}:${filter.isScheduled}:${filter.amountSign}:${filter.payee}
      :${JSON.stringify(filter.accountIdsArray)}:${filter.accountIds && filter.accountIds.hashCode()}
      :${filter.rootCOAs && filter.rootCOAs.hashCode()}:${
        filter.coas && filter.coas.hashCode && filter.coas.hashCode()
      }:${filter.startDate}:${filter.endDate}`
      : '',
  cacheObject: new LruObjectCache({ cacheSize: 13 }),
  selectorCreator: selectorsHelpers.createFlexEqualSelector,
});

export const getTransactionsByTextFilter = createSelector(
  (
    state: State,
    props: {
      transactions?: ImMap<string, TransactionRecord> | null;
      accountIds?: string[] | ImList<string> | null;
    },
  ) => props.transactions || getTransactionsForAccountIds(state, props),
  accountsSelectors.getAccountsById,
  tagsSelectors.getTags,
  (_state: unknown, props: { filter?: string | null }) => props.filter,
  (transactionsById, accountsById, tagsById, filter) => {
    const filterLowerCase = filter ? filter.toLowerCase() : undefined;
    const filterDate = filter ? Date.parse(filter) : undefined;
    let filterNumber = filter ? Number(filter) : undefined;
    filterNumber = Number.isNaN(filterNumber) ? undefined : Math.round(Math.abs(filterNumber as number));

    const transactionsByIdFiltered =
      !filterLowerCase || !filterLowerCase.length
        ? transactionsById
        : transactionsById.filter((transaction) => {
            let keepTransaction = false;

            if (!keepTransaction && transaction.payee && transaction.payee.toLowerCase().includes(filterLowerCase)) {
              keepTransaction = true;
            }
            if (!keepTransaction) {
              const account = accountsById.get(transaction.accountId || '');
              if (account && account.name?.toLowerCase().includes(filterLowerCase)) {
                keepTransaction = true;
              }
            }
            if (!keepTransaction && searchItemForText(transaction, filterLowerCase, tagsById, filterNumber)) {
              keepTransaction = true;
            }
            if (!keepTransaction && transaction.split && transaction.split.items) {
              if (transaction.split.items.find((item) => searchItemForText(item, filterLowerCase, tagsById))) {
                keepTransaction = true;
              }
            }
            if (!keepTransaction && filterDate && filterDate === Date.parse(transaction.postedOn)) {
              keepTransaction = true;
            }

            return keepTransaction;
          });

    return transactionsByIdFiltered;
  },
);

const searchItemForText = (
  item: SplitItemProps | TransactionProps,
  filterText?: string,
  tagsById?: ReturnType<typeof tagsSelectors.getTags>,
  filterNumber?: number,
) => {
  let found = false;

  if (!found && filterText && item.memo && item.memo.toLowerCase().includes(filterText)) {
    found = true;
  }
  if (!found && filterNumber !== undefined && Math.round(Math.abs(Number(item.amount ?? 0))) === filterNumber) {
    found = true;
  }
  if (!found) {
    const coaString = chartOfAccountsSelectors.getCoaStringSelector(undefined, item.coa, true);
    if (coaString && filterText && coaString.toLowerCase().includes(filterText)) {
      found = true;
    }
  }
  if (!found && item.tags && tagsById) {
    if (
      item.tags.find((tagRef) => {
        const tag = tagsById.get(tagRef.id || '');
        return Boolean(tag && filterText && tag.name?.toLowerCase().includes(filterText));
      })
    ) {
      found = true;
    }
  }

  return found;
};

// getTransactionsByAccountIdsSelectors
//
// We are using a selector based on accounts to return a map of selectors that
// can then be used to get transactions for a specific account. This map of
// selectors are stored in a global cache based on the current login.
//
// We are using this level of indirection and a global cache so that each account
// will have it's own selector for the retrieval of transactions which will not
// be invalided by the adding/removing of accounts, the updating of the account
// or changing transaction data within the account.
//
// The selectors returned by this method are not typical selectors. Instead of
// a state and props, the expected arguments are a map of transactions by
// account ID and options map. Currently the only supported option are
// sortBy and sortOrder.
//
export const getTransactionsByAccountIdsSelectors = createSelector(
  accountsSelectors.getAccountsById,
  (function combinerClosure() {
    const transactionsByAccountIdsSelector = new Map();
    return (accounts: ReturnType<typeof accountsSelectors.getAccountsById>) => {
      const activeAccountIds: string[] = [];
      accounts.forEach((account) => {
        activeAccountIds.push(getId(account));

        // if selector doesn't exist, add it
        if (!transactionsByAccountIdsSelector.get(getId(account))) {
          const selector = createSelector(
            (transactionsByAccountId) => transactionsByAccountId.get(getId(account)),
            (_transactionsByAccountId, options: any) => options,
            (txns) => {
              // was (txns, options) if you need the options

              // handle special case of no transactions in account
              if (txns === undefined || txns.size === 0) {
                return ImList([]);
              }
              return ImList(txns.toList());
            },
          );

          transactionsByAccountIdsSelector.set(getId(account), selector);
        }
      });
      return ImMap(transactionsByAccountIdsSelector);
    };
  })(),
);

export const isIncomeTxn = createSelector(
  (_state: unknown, txn: TransactionProps | null | undefined) => txn,
  categoriesSelectors.getIncomeCOAIds,
  transactionsUtils.isIncomeFromTxn,
);

// used for on-the-fly creation in transaction details
export const getCreatedTxnByClientAndAccountId = createSelector(
  (_state: unknown, clientId: string | undefined) => clientId,
  (state: State, _clientId: string | undefined, accountId: string) =>
    getTransactionsForAccountIds(state, { accountIds: [accountId] }),
  (clientId, txns) => {
    if (clientId) {
      return txns.find((item) => item.clientId === clientId && item.clientId !== item.id);
    }
    return undefined;
  },
);

export const getTxnByMatchedTxnAndAccountId = createSelector(
  (_state: unknown, matchedTxnId: string) => matchedTxnId,
  (state: State, _matchedTxnId: string, accountId: string) =>
    getTransactionsForAccountIds(state, { accountIds: [accountId] }),
  (matchedTxnId, txns) => {
    if (matchedTxnId) {
      return txns.find((item) => item.matchedTxn?.id === matchedTxnId);
    }
    return undefined;
  },
);

const getImmutableListFromIds = weakMapMemoize((ids?: string[]) => ImList<string>(ids));

// currently only used in F2S
export const getSortedTransactionsByIds = createWeakMapSelector(
  (state: State, ids: ImList<string> | string[] | undefined) =>
    getTransactionsByIds(state, ImList.isList(ids) ? ids : getImmutableListFromIds(ids)),
  (txns) =>
    txns?.sort((a: TransactionRecord, b: TransactionRecord) => {
      if (a?.postedOn >= b?.postedOn) {
        return 1;
      }
      return -1;
    }),
);

export const getLastReductionsChangeListLatestChange = createSelector(getLastReductions, (lastReductions) =>
  lastReductions?.get('changeList')?.last(),
);

export const TransactionBucketEnum = Object.freeze({
  EXPENSE: 'EXPENSE',
  INCOME: 'INCOME',
  TRANSFER: 'TRANSFER',
});

export const getTransactionBucket = createCachedSelector(
  (_state: unknown, transaction: TransactionProps) => transactionsUtils.isSplitTxn(transaction),
  (state: State, transaction: TransactionProps) =>
    transaction.coa && transaction.coa.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY
      ? categoriesSelectors.isIncomeCat(state, transaction.coa.id)
      : undefined,
  (_state: unknown, transaction: TransactionProps) => transaction.coa,
  (_state: unknown, transaction: TransactionProps) => transaction.amount,
  (isSplit, isIncome, coa, amount) => {
    let coaType;
    if (isSplit) {
      coaType = Number(amount) > 0 ? TransactionBucketEnum.INCOME : TransactionBucketEnum.EXPENSE;
    } else {
      switch (coa && coa.type) {
        case 'CATEGORY':
          coaType = isIncome ? TransactionBucketEnum.INCOME : TransactionBucketEnum.EXPENSE;
          break;
        case 'ACCOUNT':
        case 'BALANCE_ADJUSTMENT':
          coaType = TransactionBucketEnum.TRANSFER;
          break;
        case 'UNCATEGORIZED':
          coaType = TransactionBucketEnum.EXPENSE;
          break;
        default:
          coaType = TransactionBucketEnum.EXPENSE;
          break;
      }
    }
    return coaType;
  },
)({
  keySelector: (_state: unknown, transaction: TransactionProps) =>
    `${transactionsUtils.isSplitTxn(transaction)}:${transaction.coa && transaction.coa.type}:${
      transaction.coa && transaction.coa.id
    }:${transaction.amount}`,
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

export const getTrackingProps = createSelector(
  (state: State | undefined, newTransaction: TransactionRecord) =>
    getTransactionById(state || store!.getState(), getId(newTransaction)),
  (_state: unknown, newTransaction: TransactionRecord) => newTransaction,
  (existingTransaction, newTransaction) => {
    const baseTransaction = (existingTransaction || newTransaction.clear())?.toJS?.() as { [key: string]: unknown };
    const fields = newTransaction.isDeleted
      ? ['isDeleted']
      : Object.entries(newTransaction.toJS()).reduce(
          (result, [key, value]) =>
            isEqualWith(
              value,
              baseTransaction?.[key],
              (obj1, obj2) => isEqual(obj1, obj2) || obj1 == null || (!obj1 && !obj2),
            )
              ? result
              : [...result, key],
          [] as string[],
        );

    let type;
    if (newTransaction?.id && newTransaction.id !== '0') {
      if (baseTransaction?.type === 'INVESTMENT') {
        type = 'investment txn';
      } else {
        type = baseTransaction.cpData ? 'edit downloaded' : 'edit manual';
      }
    } else {
      type = 'new manual';
    }

    return {
      investmentTxnType: newTransaction.investmentTxnType,
      state: 'complete',
      type,
      fields,
    };
  },
);

export const getSecuritiesUsage = createSelector(
  getSecuritiesResourcesById,
  (state: State) => getTransactionsOfTypes(state, TxnTypes.INVESTMENT_TXN),
  investmentHoldingsV2Selectors.getInvestmentHoldingNodes,
  (securities, transactions, investmentHoldingNodes) => {
    // FIXME: add types to investment holdings and remove the any
    const holdingsCount = securities.map((security) =>
      investmentHoldingNodes.reduce(
        (usageCount: number, invHolding: any) =>
          usageCount +
          (invHolding.holdings[0].localSecurityId === security.id || invHolding.name === security.name ? 1 : 0),
        0,
      ),
    );
    const txnsCount = securities.map((security) =>
      transactions.reduce(
        (usageCount, transaction) => usageCount + (transaction.localSecurityId === security.id ? 1 : 0),
        0,
      ),
    );
    const merged = securities.map((security) =>
      txnsCount.reduce(
        () => (txnsCount?.get(security.id || '') || 0) + (holdingsCount?.get(security.id || '') || 0),
        0,
      ),
    );
    return { merged, txnsCount, holdingsCount };
  },
);

type BillableTransactionsData = {
  billableTxns: TransactionProps[];
  nonBillableTxns: TransactionProps[];
  billableSum: number;
  nonBillableSum: number;
};

export const getBillableTransactionsByPropertyIds = createAppSelector(
  [getTransactionsById, (_state, propertyId: keyof TransactionProps | keyof SplitItemProps) => propertyId],
  (transactionsById, propertyId) => {
    const property = propertyId ?? 'projectId';
    const transactionsByPropertyId: { [propertyId: string]: BillableTransactionsData } = {};

    // Because we're passing in a dynamic property that could be a key of either
    // a Transaction or a Split Item, we type this as any. We don't really care
    // if it's a transaction since indexing a property that doesn't exist still
    // works for our boolean checks.
    const setTxnsByProject = (txn: { [key: string]: any }, overallTransaction: TransactionProps) => {
      if (txn[property]) {
        if (!transactionsByPropertyId[txn[property]]) {
          transactionsByPropertyId[txn[property]] = {
            billableTxns: [],
            nonBillableTxns: [],
            billableSum: 0,
            nonBillableSum: 0,
          };
        }
        transactionsByPropertyId[txn[property]][txn.isBillable ? 'billableTxns' : 'nonBillableTxns'].push(
          overallTransaction,
        );
        transactionsByPropertyId[txn[property]][txn.isBillable ? 'billableSum' : 'nonBillableSum'] += Number(
          txn.amount,
        );
      } else {
        if (!transactionsByPropertyId['noProperty']) {
          transactionsByPropertyId['noProperty'] = {
            billableTxns: [],
            nonBillableTxns: [],
            billableSum: 0,
            nonBillableSum: 0,
          };
        }
        transactionsByPropertyId['noProperty'][txn.isBillable ? 'billableTxns' : 'nonBillableTxns'].push(
          overallTransaction,
        );
        transactionsByPropertyId['noProperty'][txn.isBillable ? 'billableSum' : 'nonBillableSum'] += Number(
          overallTransaction.amount,
        );
      }
    };

    transactionsById?.forEach((transaction) => {
      const splitValues = transactionsUtils.splitsWithPropertyValue(
        transaction.split,
        property as keyof SplitItemProps,
      );
      if ('size' in splitValues ? splitValues.size > 0 : splitValues.length > 0) {
        splitValues?.forEach((split) => setTxnsByProject(split, transaction));
      } else {
        setTxnsByProject(transaction, transaction);
      }
    });

    return transactionsByPropertyId;
  },
  {
    memoize: lruMemoize,
    memoizeOptions: {
      equalityCheck: (a, b) => {
        if (a === b) {
          return true;
        }
        if (a && b) {
          if (typeof a === 'string' && typeof b === 'string') {
            return a === b;
          }
          if (typeof a === 'object' && typeof b === 'object') {
            return (
              a.size === b.size &&
              a?.every((value: any, key: string) => {
                const bValue = b.get(key);
                return (
                  bValue.businessId === value.businessId &&
                  bValue.projectId === value.projectId &&
                  bValue.customerId === value.customerId &&
                  bValue.isBillable === value.isBillable &&
                  bValue.amount === value.amount &&
                  (bValue.split ? bValue.split.equals(value.split) : true)
                );
              })
            );
          }
        }
        return false;
      },
      maxSize: 10,
    },
  },
);

// deprecated - this assumed only one AR account
export const getTxnsByIdInAr = createSelector(
  accountsSelectors.getAccountsReceivable,
  getTransactionsByAccountId,
  (account, txnsByAccountIds) => {
    return account?.id
      ? txnsByAccountIds.get(getId(account), ImMap<string, TransactionRecord>())
      : ImMap<string, TransactionRecord>();
  },
);

export const isTxnInAr = createSelector(accountsSelectors.getAllArAccounts, getTransactionById, (arAccounts, txn) => {
  if (!arAccounts || arAccounts.isEmpty() || !txn) {
    return false;
  }
  return arAccounts.some((account) => getId(account) === txn.accountId);
});

export const getTxnsByIdInArAccounts = createSelector(
  [
    accountsSelectors.getAllArAccounts,
    (state: State, props?: { txnTypes?: number }) =>
      getTransactionsByAccountId(state, { txnTypes: props?.txnTypes ?? txnTypesDefault }),
  ],
  (arAccounts, txnsByAccountIds) => {
    if (!arAccounts || arAccounts.isEmpty()) {
      return ImMap<string, TransactionRecord>(); // Return empty map if no AR accounts
    }

    // Accumulate transactions from all AR accounts into a single map
    return arAccounts.reduce((acc, account) => {
      const accountTxns = txnsByAccountIds.get(getId(account), ImMap<string, TransactionRecord>());
      return acc.merge(accountTxns);
    }, ImMap<string, TransactionRecord>());
  },
);

// deprecated - this assumed only one AR account
export const getTxnsInArByCustomerId = createSelector(getTxnsByIdInAr, (txnsByIdInAr) =>
  txnsByIdInAr.groupBy((txn) => txn.customerId ?? 'no-customer'),
);

export const getTxnsInArAccountsByCustomer = createSelector(getTxnsByIdInArAccounts, (txnsById) =>
  txnsById.groupBy((txn) => txn.customerId ?? 'no-customer'),
);

export const getTxnsInArAccountsByBusinessByCustomer = createSelector(getTxnsByIdInArAccounts, (txnsById) =>
  txnsById
    .groupBy((txn: TransactionRecord) => txn.businessId ?? 'no-business') // First group by businessId
    .map(
      (txnsByBusinessId) => txnsByBusinessId.groupBy((txn: TransactionRecord) => txn.customerId ?? 'no-customer'), // Then group by customerId within each businessId
    ),
);

// deprecated - this assumed only one AR account
export const getTxnsInArByProjectId = createSelector(getTxnsByIdInAr, (txnsByIdInAr) =>
  txnsByIdInAr.groupBy((txn) => txn.projectId ?? `${txn.customerId}-no-project` ?? 'no-project'),
);

export const getTxnsInArAccountsByProjectId = createSelector(getTxnsByIdInArAccounts, (txnsByIdInAr) =>
  txnsByIdInAr.groupBy((txn) => txn.projectId ?? `${txn.customerId}-no-project` ?? 'no-project'),
);

export const getTxnsInArForProjectId = createWeakMapSelector(
  getTxnsInArAccountsByProjectId,
  (_state: unknown, projectId: string) => projectId,
  (txnsByProjectId, projectId) => txnsByProjectId.get(projectId) ?? ImList<TransactionRecord>(),
);

export const isAccountsReceivableTxn = createSelector(
  [(_state: unknown, props: { txn: TransactionRecord }) => props.txn, accountsSelectors.getAllArAccountIds],
  (txn, arAccountIds) => arAccountIds.includes(txn.accountId!),
);

// Examples: Payments, Credit-Memo, Bad Debt & Sales Tax txns
export const isQBPCreditTxn = createSelector(
  [(_state: unknown, props: { txn: TransactionProps }) => props.txn, accountsSelectors.getAccountsById],
  (txn, accounts) => {
    const account = txn.accountId ? accounts.get(txn.accountId) : null;
    return account && txn.type === 'CASH_FLOW' && isQBPAccount(account) && txn.amount < -0.005;
  },
);

// Examples: Payments, Credit-Memo, Bad Debt
export const isAccountsReceivableCreditTxn = createSelector(
  [(_state: unknown, props: { txn: TransactionProps }) => props.txn, accountsSelectors.getAllArAccountIds],
  (txn, arAccountIds) => txn.type === 'CASH_FLOW' && arAccountIds.includes(txn.accountId!) && txn.amount < -0.005,
);

// Examples: Payments, Unearned Income (transfer from A/R)
export const isOtherSideOfAccountsReceivableCreditTxn = createSelector(
  [(_state: unknown, props: { txn: TransactionRecord }) => props.txn, accountsSelectors.getAllArAccountIds],
  (txn, arAccountIds) =>
    txn.type === 'CASH_FLOW' && txn.coa?.type === 'ACCOUNT' && arAccountIds.includes(txn.coa.id) && txn.amount > 0.005,
);

// Examples: Invoices, Refunds, Unearned Income
export const isAccountsReceivableDebitTxn = createSelector(
  [(_state: unknown, props: { txn: TransactionProps }) => props.txn, accountsSelectors.getAllArAccountIds],
  (txn, arAccountIds) => txn.type === 'CASH_FLOW' && arAccountIds.includes(txn.accountId!) && txn.amount > -0.005,
);

// Examples: Refunds, Unearned Income (transfer from Unearned Income)
export const isOtherSideOfAccountsReceivableDebitTxn = createSelector(
  [(_state: unknown, props: { txn: TransactionRecord }) => props.txn, accountsSelectors.getAllArAccountIds],
  (txn, arAccountIds) =>
    txn.type === 'CASH_FLOW' && txn.coa?.type === 'ACCOUNT' && arAccountIds.includes(txn.coa.id) && txn.amount < -0.005,
);

export const isInvoicePaymentTransfer = createSelector(
  [isAccountsReceivableTxn, isOtherSideOfAccountsReceivableCreditTxn],
  (a, b) => a || b,
);

export const isInvoicePaymentSplit = createSelector(
  [
    getAccountsById,
    (_: unknown, args: { item: SplitItemProps }) => args.item.coa,
    (_: unknown, args: { item: SplitItemProps }) => args.item.amount ?? 0,
  ],
  (accounts, coa, amount) =>
    amount > 0 && coa?.type === 'ACCOUNT' && !!coa.id && accounts.get(coa.id)?.type === 'ACCOUNTS_RECEIVABLE',
);

const sumUnpaidAmount = (sum: number, txn: TransactionRecord) => {
  if (transactionsUtils.isInvoiceTxn(txn)) {
    let paid = 0.0;
    if (Array.isArray(txn.allocations)) {
      paid = txn.allocations.reduce<number>(
        (s: number, allocation: AllocationProps) => s + (allocation.amount ?? 0),
        0,
      );
    } else if (txn.allocations instanceof ImList) {
      paid = txn.allocations.reduce<number>((s: number, allocation: Allocation) => s + (allocation.amount ?? 0), 0);
    }
    const unpaid = Number(txn.amount) - paid;
    return sum + unpaid;
  }
  return sum;
};

export const getBilledSumForProjectId = createWeakMapSelector(
  getTxnsInArForProjectId,
  (txns) => (txns as ImList<TransactionRecord>)?.reduce<number>(sumUnpaidAmount, 0) ?? 0,
);

export const getTxnsInArForCustomerId = createWeakMapSelector(
  getTxnsInArAccountsByCustomer,
  (_state: unknown, customerId: string) => customerId,
  (txnsByCustomerId, customerId) => txnsByCustomerId.get(customerId) ?? ImList<TransactionRecord>(),
);

export const getBilledSumForCustomerId = createWeakMapSelector(
  getTxnsInArForCustomerId,
  (txns) => (txns as ImList<TransactionRecord>)?.reduce(sumUnpaidAmount, 0) ?? 0,
);

export const getLinkedTxnsForProjectId = createSelector(
  (state) => getTransactionsOfTypes(state, txnTypesCash),
  (_state, props) => props.projectId,
  (_, props) => props.customerId,
  (txns, projectId, customerId) => {
    const txnList = txns?.toList() ?? ImList<TransactionRecord>();
    return txnList.filter((txn: TransactionRecord) => {
      if (txn.subtype === 'INVOICE_PAYMENT' || transactionsUtils.isInvoiceTxn(txn)) {
        return false;
      }
      if (Array.isArray(txn.split?.items) ? txn.split.items.length > 0 : (txn.split?.items?.size ?? 0) > 0) {
        return Boolean(
          txn.split?.items?.some(
            (item: SplitItemProps) =>
              !!(item.projectId === projectId) || (projectId === 'no-project' && item.customerId === customerId),
          ),
        );
      }

      return !!(
        txn?.get('projectId') === projectId ||
        (projectId === 'no-project' && txn?.get('customerId') === customerId && !txn?.get('projectId'))
      );
    });
  },
);

export const getTransactionByItemId = createSelector(
  getResourcesById,
  (_state, itemId) => itemId,
  (transactions, itemId) =>
    transactions.get(itemId) ||
    transactions.find((transaction: RecordOf<TransactionProps>) =>
      transaction.split?.items?.find((splitItem) => splitItem.id === itemId),
    ),
);

export const getBilledItemsForProjectId = createSelector(getTxnsInArForProjectId, (txns) => {
  const retArr: { item: SplitItemProps; invoice: TransactionRecord }[] = [];
  txns?.forEach((txn) => {
    if (transactionsUtils.isInvoiceTxn(txn) || txn.subtype === 'INVOICE_CREDIT_MEMO') {
      txn.split?.items?.forEach((item) => {
        if (item.type === 'INVOICE_BILLABLE_ITEM' || item.type === 'INVOICE_BILLABLE_EXPENSE') {
          retArr.push({ item, invoice: txn });
        }
      });
    }
  });
  return retArr;
});

export const getBilledBillableItemsForProjectId = createSelector(getTxnsInArForProjectId, (txns) => {
  const retArr: { item: SplitItemProps; invoice: TransactionRecord }[] = [];
  txns?.forEach((txn) => {
    if (transactionsUtils.isInvoiceTxn(txn)) {
      txn.split?.items?.forEach((item) => {
        if (item.type === 'INVOICE_BILLABLE_ITEM') {
          retArr.push({ item, invoice: txn });
        }
      });
    }
  });
  return retArr;
});

// flattens transactions into split items list (TransactionItems)
export const getTransactionItemsById = createSelector(
  getResourcesById,
  (function combiner() {
    const transactionCache = new WeakMap<TransactionRecord, TransactionItem>();
    const transactionSplitCache = new WeakMap<SplitItemProps, TransactionItem>();
    return (transactions: ImMap<string, TransactionRecord>) => {
      return ImMap<string, TransactionItem>().withMutations((map) => {
        transactions.forEach((transaction, id) => {
          if (!assert(ImRecord.isRecord(transaction), 'transaction is not a record in getTransactionItemsById')) return;
          if (
            transaction?.split?.items &&
            (Array.isArray(transaction?.split?.items)
              ? transaction.split.items.length
              : transaction?.split?.items?.size)
          ) {
            transaction?.split?.items.forEach((splitItem) => {
              // If there is a previously cached item for this split (ie no changes made) re-use the last one
              const transactionItem =
                transactionSplitCache.get(splitItem) ??
                mkTransactionItem({
                  transaction,
                  splitItem: splitItem as RecordOf<SplitItemProps>,
                  item: {
                    ...transaction?.toObject(),
                    ...(ImRecord.isRecord(splitItem) ? splitItem?.toObject() : splitItem),
                  },
                });
              if (!transactionSplitCache.has(splitItem)) {
                transactionSplitCache.set(splitItem, transactionItem);
              }
              map.set(String(splitItem?.id || splitItem?.clientId), transactionItem);
            });
          } else {
            const transactionItem =
              transactionCache.get(transaction) ?? mkTransactionItem({ transaction, item: transaction?.toObject() });
            if (!transactionCache.has(transaction)) {
              transactionCache.set(transaction, transactionItem);
            }
            map.set(id, transactionItem);
          }
        });
      });
    };
  })(),
);

export const getTransactionItemsByIdOfTypes = createSelector(
  [
    getTransactionItemsById,
    accountsSelectors.getGoalAccountIds,
    (_state: unknown, txnTypes: number | undefined) => txnTypes || txnTypesDefault,
    getCurrentDateString,
  ],
  (transactionItemsById, goalAccountIds, txnTypes, todayISO) =>
    transactionItemsById.filter((transactionItem) => {
      transactionOfTypes(transactionItem.transaction, goalAccountIds, txnTypes, todayISO);
    }),
);

// todo - remove when main txn register shows splits as new line
// flattens transactions into TransactionItems for main register
export const getTransactionItemsByIdWithoutSplits = createSelector(
  getResourcesById,
  (function combiner() {
    let transactionItemsByIdPrev = ImMap<string, TransactionItem>();
    return (transactions: ImMap<string, TransactionRecord>) => {
      const transactionItemsById = transactions
        ?.filter((transaction) =>
          assert(ImRecord.isRecord(transaction), 'transaction is not a record in getTransactionItemsById'),
        )
        ?.flatMap((transaction, id) => {
          const transactionItemPrev = transactionItemsByIdPrev?.get(id);
          const transactionItem = mkTransactionItem({ transaction, item: transaction?.toObject() });
          return transactionItemPrev && isEqual(transactionItemPrev, transactionItem)
            ? [[id, transactionItemPrev]]
            : [[id, transactionItem]];
        });
      transactionItemsByIdPrev = ImMap<string, TransactionItem>(transactionItemsById);
      return transactionItemsByIdPrev;
    };
  })(),
);

export const getTransactionItemById = (state: State, id: string) => getTransactionItemsById(state).get(id);

// todo - remove when main txn register shows splits as new line
export const getTransactionItemByIdWithoutSplits = (state: State, id: string) =>
  getTransactionItemsByIdWithoutSplits(state).get(id);

// todo - remove when main txn register shows splits as new line
export const getTransactionItemsByIdsWithoutSplits = createCachedSelector(
  getTransactionItemsByIdWithoutSplits,
  (_state: State, transactionItemIds: ImSet<string> | null) => transactionItemIds,
  (transactionItemsById, transactionItemIds) =>
    transactionItemsById.filter((transactionItem) => transactionItemIds?.has(transactionItem.item.id as string)),
)({
  keySelector: (_state, transactionItemIds) => transactionItemIds?.hashCode() ?? 'unknown',
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

export const getTransactionItemsByIds = createCachedSelector(
  getTransactionItemsById,
  (_state: State, transactionItemIds: ImSet<string> | null) => transactionItemIds,
  (transactionItemsById, transactionItemIds) =>
    transactionItemsById.filter((transactionItem) => transactionItemIds?.has(transactionItem.item.id as string)),
)({
  keySelector: (_state, transactionItemIds) => transactionItemIds?.hashCode() ?? 'unknown',
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

export enum ProjectTransactionItemGroups {
  BALANCE_ADJUSTMENT = 'BALANCE_ADJUSTMENT',
  EXPENSE = 'EXPENSE',
  INCOME = 'INCOME',
  TRANSFER = 'TRANSFER',
  UNCATEGORIZED = 'UNCATEGORIZED',
}

// TODO: Should we exclude certain accounts or transaction types/subtypes?
//
// This selector groups transaction items by project ID and then by ProjectTransactionItemGroups. If item has
// a customerId and no projectId, it will be grouped by customerId (i.e. no-project customerId).
//
// Potential uses in downstream selectors:
//   On Client Page, sum for unbilled, billed and revenue
//   On Client Page, unbilled items and expenses (across projects)
//   On Project Page, sum for unbilled, billed, unpaid, non-billable, project value, paid
//   On Project Page, billable items, billed items and project expenses
//   On Invoice Form, need to add all billable expenses that haven't yet been billed
//
const getProjectTxnItemGroup = (coa: chartOfAccountsTypes.ChartOfAccount, categoriesById: ImMap<string, Category>) => {
  switch (coa.type) {
    case chartOfAccountsTypes.CoaTypeEnum.ACCOUNT:
      return ProjectTransactionItemGroups.TRANSFER;
    case chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT:
      return ProjectTransactionItemGroups.BALANCE_ADJUSTMENT;
    case chartOfAccountsTypes.CoaTypeEnum.CATEGORY: {
      const category = categoriesById.get(coa.id);
      if (!category) {
        return ProjectTransactionItemGroups.UNCATEGORIZED;
      }
      return category.type === 'INCOME' ? ProjectTransactionItemGroups.INCOME : ProjectTransactionItemGroups.EXPENSE;
    }
    case chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED:
      return ProjectTransactionItemGroups.UNCATEGORIZED;
    default:
      log.warn('Unexpected COA type', coa.type);
      return undefined;
  }
};

// Helper function to process transactions
const processTransactionItem = (
  projectId: string,
  txn: TransactionRecord,
  item: RecordOf<SplitItemProps> | null,
  categoriesById: ImMap<string, Category>,
  tempMap: Map<string, Map<`${ProjectTransactionItemGroups}`, TransactionItem[]>>,
) => {
  const coa = item ? item.coa : txn.coa;
  const coaGroup = coa ? getProjectTxnItemGroup(coa as chartOfAccountsTypes.ChartOfAccount, categoriesById) : undefined;
  if (!coaGroup) return;

  const txnItem = mkTransactionItem({
    transaction: txn,
    splitItem: item ? item : undefined,
    item: {
      ...txn.toObject(),
      ...(item ? item.toObject() : {}),
    },
  });

  if (!tempMap.has(projectId)) {
    tempMap.set(projectId, new Map<ProjectTransactionItemGroups, TransactionItem[]>());
  }
  const projectMap = tempMap.get(projectId)!;

  if (!projectMap.has(coaGroup)) {
    projectMap.set(coaGroup, []);
  }
  projectMap.get(coaGroup)?.push(txnItem);
};

// Convert tempMap to Immutable Map
const processTempMap = (tempMap: Map<string, Map<`${ProjectTransactionItemGroups}`, TransactionItem[]>>) => {
  return ImMap<string, ImMap<`${ProjectTransactionItemGroups}`, ImList<TransactionItem>>>().withMutations((map) => {
    tempMap.forEach((coaGroupMap, projectId) => {
      map.set(
        projectId,
        ImMap<`${ProjectTransactionItemGroups}`, ImList<TransactionItem>>().withMutations((coaMap) => {
          coaGroupMap.forEach((items, coaGroup) => {
            coaMap.set(coaGroup, ImList(items));
          });
        }),
      );
    });
  });
};

const groupTransactionItemGroupsByKey = (
  txnsById: ImMap<string, TransactionRecord>,
  categoriesById: ImMap<string, Category>,
  keyExtractor: (item: TransactionRecord | SplitItemProps) => string | undefined,
) => {
  // Temporary storage for project transactions grouped by coaGroup
  const tempMap = new Map<string, Map<`${ProjectTransactionItemGroups}`, TransactionItem[]>>();

  // Iterate over transactions
  txnsById.forEach((txn: TransactionRecord) => {
    if (txn.split?.items) {
      txn.split.items.forEach((splitItem) => {
        const item = splitItem as RecordOf<SplitItemProps>;
        const key = keyExtractor(item);
        if (key) {
          processTransactionItem(key, txn, item, categoriesById, tempMap);
        }
      });
    } else {
      const key = keyExtractor(txn);
      if (key) {
        processTransactionItem(key, txn, null, categoriesById, tempMap);
      }
    }
  });

  return processTempMap(tempMap);
};

export const getTransactionItemGroupsByProjectId = createSelector(
  [getResourcesById, categoriesSelectors.getCategoriesById],
  (txnsById, categoriesById) => {
    return groupTransactionItemGroupsByKey(
      txnsById,
      categoriesById,
      (item) => (item.projectId || item.customerId) ?? undefined,
    );
  },
);
export const getTransactionItemGroupsByCustomerId = createSelector(
  [getResourcesById, categoriesSelectors.getCategoriesById],
  (txnsById, categoriesById) => {
    return groupTransactionItemGroupsByKey(txnsById, categoriesById, (item) => item.customerId ?? undefined);
  },
);

const emptyTransactionItems = ImList<TransactionItem>();

type GetExpensesSelectorProps = {
  projectId?: string | null;
  customerId?: string | null;
  excludeNonBillable?: boolean;
  excludeBilled?: boolean;
  excludeUnbilled?: boolean;
};

const getExpenseTxnItems = (
  txnItemGroup?: ImMap<`${ProjectTransactionItemGroups}`, ImList<TransactionItem>>,
  excludeNonBillable?: GetExpensesSelectorProps['excludeNonBillable'],
  excludeUnbilled?: GetExpensesSelectorProps['excludeUnbilled'],
  excludeBilled?: GetExpensesSelectorProps['excludeBilled'],
) => {
  const expenses = txnItemGroup?.get(ProjectTransactionItemGroups.EXPENSE);
  const uncategorized = txnItemGroup?.get(ProjectTransactionItemGroups.UNCATEGORIZED);

  if (!expenses && !uncategorized) return emptyTransactionItems;

  return (expenses || ImList<TransactionItem>())
    .concat(uncategorized || ImList<TransactionItem>())
    .filter((txnItem) => {
      // Include a cash flow txn, its splits (type normal), or a invoice txn split of type billable expense
      if (
        txnItem &&
        (txnItem.item.type === 'INVOICE_BILLABLE_EXPENSE' ||
          txnItem.item.type === 'CASH_FLOW' ||
          txnItem.item.type === 'NORMAL')
      ) {
        if (
          ((excludeNonBillable
            ? transactionsUtils.isNonBilledBillableItem(txnItem)
            : transactionsUtils.isNonBilledItem(txnItem)) ||
            (!excludeBilled && transactionsUtils.isBilledItem(txnItem))) &&
          !(excludeUnbilled && transactionsUtils.isNonBilledBillableItem(txnItem))
        ) {
          return true;
        }
      }
      return false;
    });
};

export const getExpensesForProjectId = createSelector(
  [
    getTransactionItemGroupsByProjectId,
    (_state: any, props: GetExpensesSelectorProps) => props.projectId,
    (_state: any, props: GetExpensesSelectorProps) => props.customerId,
    (_state: any, props: GetExpensesSelectorProps) => props.excludeNonBillable,
    (_state: any, props: GetExpensesSelectorProps) => props.excludeUnbilled,
    (_state: any, props: GetExpensesSelectorProps) => props.excludeBilled,
  ],
  (txnItemGroups, projectId, customerId, excludeNonBillable, excludeUnbilled, excludeBilled) => {
    const id = projectId && projectId !== 'no-project' ? projectId : customerId;
    if (!id) return emptyTransactionItems;

    return getExpenseTxnItems(txnItemGroups.get(id), excludeNonBillable, excludeUnbilled, excludeBilled);
  },
);

export const selectUnbilledProjectExpenses = (state: any, { customerId, projectId }: GetExpensesSelectorProps) => {
  return getExpensesForProjectId(state, { customerId, projectId, excludeBilled: true, excludeNonBillable: true });
};

export const getExpensesForCustomerId = createSelector(
  [
    getTransactionItemGroupsByCustomerId,
    (_state: any, props: GetExpensesSelectorProps) => props.customerId,
    (_state: any, props: GetExpensesSelectorProps) => props.excludeNonBillable,
    (_state: any, props: GetExpensesSelectorProps) => props.excludeUnbilled,
    (_state: any, props: GetExpensesSelectorProps) => props.excludeBilled,
  ],
  (txnItemGroups, customerId, excludeNonBillable, excludeUnbilled, excludeBilled) => {
    if (!customerId) return emptyTransactionItems;

    return getExpenseTxnItems(txnItemGroups.get(customerId), excludeNonBillable, excludeUnbilled, excludeBilled);
  },
);

export const selectUnbilledCustomerExpenses = (state: any, { customerId }: GetExpensesSelectorProps) => {
  return getExpensesForCustomerId(state, { customerId, excludeBilled: true, excludeNonBillable: true });
};

export const getTransactionsForLinkedProjectDialog = createSelector(
  [(state) => getTransactionsOfTypes(state, txnTypesCash), categoriesSelectors.getIncomeCOAIds],
  (txns, incomeCOAs) => {
    return txns
      ?.filter(
        (txn) =>
          (txn.projectId === null || txn.projectId === undefined) &&
          !transactionsUtils.isIncomeFromTxn(txn, incomeCOAs) &&
          !transactionsUtils.isUncategorizedTxn(txn) &&
          !transactionsUtils.isInvoiceTxn(txn) &&
          txn.subtype !== 'INVOICE_PAYMENT',
      )
      ?.toList()
      ?.sort((a, b) => (a.postedOn < b.postedOn ? 1 : -1));
  },
);

export const getTransactionItemsFiltered = createCachedSelector(
  getTransactionItemsById,
  (_state: State, filterObject: filtersTypes.FilterObject) => filterObject,
  (transactionItemsById, filterObject) =>
    transactionItemsById.filter((transactionItem) =>
      transactionsUtils.doesTxnMatchFilterObj(transactionItem, filterObject),
    ),
)({
  keySelector: (_state, filterObject: filtersTypes.FilterObject) => filterObject?.hashCode(),
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

const getFilteredAccountIds = createSelector(
  (state: State, props: { accountIds: ImList<string> }) => props.accountIds,
  (state: State) => featureFlagsSelectors.getFeatureFlag(state, 'hideConnectedLoanTransactions'),
  (accountIds, hideConnectedLoanTransactions) =>
    ImList(
      accountIds.filter((accountId) =>
        store
          ? !accountsUtils.transactionsNotSupportedForAccountId(
              accountId,
              hideConnectedLoanTransactions,
              store?.getState(),
            )
          : true,
      ),
    ),
);

export const getTransactionItemsFilteredWithoutSplits = createCachedSelector(
  // todo - change when main txn register shows splits as new line -- for main register
  [
    getTransactionItemsByIdWithoutSplits,
    (_state: State, props: { filterObject: filtersTypes.FilterObject }) => props.filterObject,
    accountsSelectors.getGoalAccountIds,
    getFilteredAccountIds,
    dateSelectors.getCurrentDateString,
  ],
  (transactionItemsById, filterObject, goalAccountIds, accountIds, todayString) =>
    transactionItemsById.filter(
      ({ transaction }) =>
        transaction.postedOn <= todayString &&
        accountIds.includes(transaction.accountId || '') &&
        !transactionsUtils.isUnacceptedScheduledTxn(transaction) &&
        transactionsUtils.doesTxnMatchFilterObj(transaction, filterObject) &&
        transactionOfTypes(transaction, goalAccountIds, txnTypesDefault, todayString),
    ),
)({
  keySelector: (_state, props: { filterObject: filtersTypes.FilterObject }) => props.filterObject?.hashCode(),
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

export const getTransactionItemsByTransactionIds = createCachedSelector(
  getTransactionItemsById,
  (_state: State, transactionIds: ImSet<string> | null) => transactionIds,
  (transactionItemsById, transactionIds) =>
    transactionItemsById.filter((transactionItem) => transactionIds?.has(transactionItem.transaction.id as string)),
)({
  keySelector: (_state, transactionIds) => transactionIds?.hashCode() ?? 'unknown',
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

export const isTransactionItemReportableIncome = createSelector(
  [
    (_state: unknown, transactionItem: TransactionItemCombinedProps) => transactionItem,
    categoriesSelectors.getCategoriesById,
  ],
  (txn, categoriesById) => transactionsUtils.isTransactionItemReportableIncomeUtil(txn, categoriesById),
);

export const getTransactionStatusLabel = createSelector(
  [
    (_state: unknown, transactionItem: TransactionItemCombinedProps) => transactionItem,
    (_state: unknown, transactionItem: TransactionItemCombinedProps) => transactionItem.postedOn,
    dateSelectors.getCurrentDateString,
    (state: Pick<RootState, typeof ACCOUNTS_REDUCER_KEY>, transactionItem: TransactionItemCombinedProps) =>
      chartOfAccountsSelectors.isCreditBill(state, transactionItem.accountId, transactionItem.coa),
    isTransactionItemReportableIncome,
  ],
  (txn, postedOn, currentDay, isCredit, isIncome) => {
    const postedDate = DateTime.fromISO(postedOn).startOf('day');
    const pending = transactionsUtils.isUpcoming(txn, currentDay);
    const today = DateTime.fromISO(currentDay);

    const isTransfer = chartOfAccountsUtils.isTransferCOA(txn.coa);

    let myStatus = '';
    if (pending) {
      if (postedDate.hasSame(today, 'day')) {
        myStatus = StatusChipTypes.TODAY;
      } else if (postedDate > today) {
        myStatus = StatusChipTypes.UPCOMING;
      } else {
        myStatus = StatusChipTypes.PAST;
      }
    } else {
      myStatus = StatusChipTypes.PAID;
      if (isIncome) {
        myStatus = StatusChipTypes.RECEIVED;
      }
      if (isCredit) {
        myStatus = StatusChipTypes.PAID;
      }
      if (isTransfer) {
        // Sending
        if (txn.amount < 0) {
          myStatus = StatusChipTypes.SENT;
        }

        //  Receiving
        if (txn.amount > 0) {
          myStatus = StatusChipTypes.RECEIVED;
        }

        // Impossible
        if (txn.amount === 0) {
          myStatus = StatusChipTypes.SUCCESS;
        }
      }
    }
    return myStatus;
  },
);

export const getPotentialGoalTransactions = createSelector(
  [
    (state) => getTransactionsOfTypes(state, txnTypesCashAndPlanned),
    categoriesSelectors.getIncomeCOAIds,
    tagsSelectors.getTags,
    (_state, props) => props.filter,
    (_state, props) => props.withdrawMaximum,
    (_state, props) => props.accountId,
  ],
  (txns, incomeCOAIds, tags, filter, withdrawMaximum, accountId) => {
    return txns
      .filter(
        (txn) =>
          !incomeCOAIds.includes(txn.coa?.id ?? '') &&
          txn.accountId === accountId &&
          txn.type !== 'INVOICE' &&
          txn?.source !== 'SCHEDULED_TRANSACTION_PENDING' &&
          txn.amount > -1 * withdrawMaximum &&
          txn.amount < 0 &&
          txn.subtype !== 'GOAL_SPENT' &&
          txn?.coa?.type !== 'ACCOUNT' &&
          !txn?.split &&
          !txn.tags?.some((tag) => tags.get(tag.id ?? '')?.type === 'GOAL') &&
          (filter === '' || txn.payee?.toLowerCase().includes(filter.toLowerCase())),
      )
      .sortBy(
        ({ postedOn }) => postedOn,
        (a, b) => b.localeCompare(a),
      );
  },
);

// TODO Maybe: Make this work with splits if we ever get to that
export const getNonTransferTransactionItemsWithTagId = createSelector(
  [getTransactionItemsById, (state, tagId) => tagId],
  (transactions, tagId) =>
    transactions.filter(
      (transactionItem) =>
        transactionItem?.transaction?.tags?.some((tag) => tag?.id === tagId) &&
        transactionItem?.transaction?.coa?.type !== 'ACCOUNT',
    ),
);

const emptyMap = ImMap();

type GoalTxnsForIdAmountAndDateProps = {
  acctId: string;
  date: string;
  amount: number;
};
export const getGoalTxnsForIdAmountAndDate = createWeakMapSelector(
  [
    (state: State, props: GoalTxnsForIdAmountAndDateProps) => getTransactionsByAccountId(state).get(props.acctId),
    (state: State, props: GoalTxnsForIdAmountAndDateProps) => props.acctId,
    (state: State, props: GoalTxnsForIdAmountAndDateProps) => props.date,
    (_, props) => props.amount,
  ],
  (goalTxns, acctIdProp, date, amount) => {
    let filteredGoalTxns = emptyMap;
    if (goalTxns && goalTxns.size > 0) {
      filteredGoalTxns = goalTxns.filter((txn) => {
        // filter goal txns by amount (passed as property to selector)
        return txn.amount === amount && txn.postedOn === date;
      });
    }
    return filteredGoalTxns;
  },
);

type CreditAccount = RecordOf<accountsTypes.CreditAccountProps>;

export const getCreditAccountFromTxn = createSelector(
  [(_, txn: TransactionProps) => txn, accountsSelectors.getAccountsById],
  (txn, accounts) => {
    if (!txn) return;
    const fromAccount = (txn?.accountId && accounts?.get(txn.accountId)) || undefined;
    const toAccount = (txn?.coa?.id && accounts?.get(txn.coa.id)) || undefined;
    if (toAccount && toAccount.type === 'CREDIT' && toAccount.isConnected) {
      return toAccount as CreditAccount;
    }
    if (fromAccount && fromAccount.type === 'CREDIT') {
      return fromAccount as CreditAccount;
    }
    // if coming from scheduledTransaction, maybe allow a provideable accountId for CC payments?
    return;
  },
);

// not using a memoizedSelector because it's just a boolean compare
export const getCanBVA = (state: State, txn: TransactionProps) => {
  if (getCreditAccountFromTxn(state, txn)) {
    return transactionsUtils.isTransferTxn(txn);
  }
  return false;
};

export const selectOtherTxnsForPanel = createSelector(
  [
    (state, props) => getTransactionItemGroupsByCustomerId(state).get(props.customerId),
    (_state, props) => props.projectId,
    getAccountsById,
  ],
  (txnItemsMap, projectId, accountsById) => {
    // want income transactions assigned to client/project
    const incomeItems = txnItemsMap?.get('INCOME') ?? ImList();
    const uncatItems = txnItemsMap?.get('UNCATEGORIZED')?.filter((txn) => txn?.item?.amount > 0) ?? ImList();

    return incomeItems
      ?.concat(uncatItems)
      .reduce((result: any[], txn: any) => {
        let projectCheck = true;
        if (projectId) {
          if (projectId !== 'no-project') {
            projectCheck = txn.item.projectId === projectId;
          } else {
            projectCheck = !txn.item.projectId;
          }
        }
        const account = accountsById.get(txn.item.accountId);

        if (
          txn?.item &&
          projectCheck &&
          txn.transaction.type !== 'INVOICE' &&
          txn.item.type !== 'INVOICE' &&
          account?.type !== 'ACCOUNTS_RECEIVABLE'
        ) {
          result.push(txn);
        }

        return result;
      }, [])
      .sort((a: any, b: any) => (a.item.postedOn < b.item.postedOn ? 1 : -1));
  },
);

export const getPreviousUnlinkedReminders = createSelector(
  [
    (state) => getTransactionsOfTypes(state, txnTypesCashAndPlanned),
    (_state, props) => props.stModelId,
    (_state, props) => props.postedOn,
  ],
  (txns, stModelId, postedOn) =>
    txns.reduce<RecordOf<TransactionProps>[]>((acc, txn) => {
      if (txn.source === 'SCHEDULED_TRANSACTION_PENDING' && txn.stModelId === stModelId && txn.postedOn < postedOn) {
        acc.push(txn);
      }
      return acc;
    }, []),
);

export const getSTModelTransactions = createSelector(
  getResourcesById,
  (state, stModelId) => stModelId,
  (transactions, stModelId) =>
    transactions.filter((transaction: TransactionRecord) => transaction.stModelId === stModelId),
);
